/**
 * Rules engine — the vocabulary and the pure helpers every rules-driven
 * feature shares (project type rules, invoice type rules, cost code rules).
 *
 * Mirrors the backend package `@mercury/mercury-rules-engine`: a rule is
 * "read `fieldPath` from `targetEntity`, apply `conditionType` against
 * `expectedValue`"; a definition is a group of rules that are ANDed, and the
 * definitions are walked in priority order — the first one whose active rules
 * all match wins. Nothing here touches React, the network or app context, so
 * it is unit-testable and the components in this folder stay presentational.
 */

/* ------------------------------------------------------------------ */
/* Vocabulary (the backend enums, as string unions)                     */
/* ------------------------------------------------------------------ */

export type RuleCondition =
  | "EQUALS"
  | "NOT_EQUALS"
  | "IN"
  | "NOT_IN"
  | "CONTAINS"
  | "NOT_CONTAINS"
  | "GREATER_THAN"
  | "LESS_THAN"
  | "GREATER_THAN_OR_EQUAL"
  | "LESS_THAN_OR_EQUAL"
  | "EXISTS"
  | "NOT_EXISTS"
  | "REGEX";

/** Every condition, in the order the backend declares them. */
export const RULE_CONDITIONS: RuleCondition[] = [
  "EQUALS",
  "NOT_EQUALS",
  "IN",
  "NOT_IN",
  "CONTAINS",
  "NOT_CONTAINS",
  "GREATER_THAN",
  "LESS_THAN",
  "GREATER_THAN_OR_EQUAL",
  "LESS_THAN_OR_EQUAL",
  "EXISTS",
  "NOT_EXISTS",
  "REGEX",
];

/** How a collection target's members are combined before the condition is applied. */
export type RuleAggregate = "COUNT_DISTINCT" | "COUNT" | "ANY" | "ALL" | "NONE";
export const RULE_AGGREGATES: RuleAggregate[] = ["COUNT_DISTINCT", "COUNT", "ANY", "ALL", "NONE"];

/** How a value is taken from a collection when a definition outputs a value from the data. */
export type RuleValueAggregate = "DISTINCT" | "FIRST";
export const RULE_VALUE_AGGREGATES: RuleValueAggregate[] = ["DISTINCT", "FIRST"];

/** The fields every rule carries, whatever it decides (the backend `RuleFields<TTarget>`). */
export interface RuleFields<TTarget extends string = string> {
  targetEntity: TTarget;
  fieldPath: string;
  conditionType: RuleCondition;
  expectedValue?: any;
  /** Required for collection targets. */
  aggregate?: RuleAggregate | string;
}

/** A stored rule: the fields plus its bookkeeping. The owning-definition key differs per feature. */
export interface RuleRecord<TTarget extends string = string> extends RuleFields<TTarget> {
  _id: string;
  description?: string;
  active: boolean;
  deleted: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** What the rule form submits. */
export interface RulePayload<TTarget extends string = string> extends RuleFields<TTarget> {
  description?: string;
  active: boolean;
}

/** The fields every definition carries; features add their own (stage, codes, …). */
export interface DefinitionRecord {
  _id: string;
  name: string;
  description?: string;
  /** Evaluation order: lower first. */
  priority: number;
  active: boolean;
  deleted: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** What an editor needs to offer: served by each feature's `/vocabulary`, or declared statically. */
export interface RuleVocabulary {
  targetEntities: string[];
  conditionTypes: RuleCondition[];
  /** Targets that are collections and need a rule `aggregate`. */
  collectionTargets?: string[];
  aggregates?: string[];
  /** Conditions whose expected value must be a number (informational; the sets below are the source of truth client-side). */
  numericConditionTypes?: string[];
}

/** One rule evaluation on the way to an answer (the backend `RuleEvaluation`). */
export interface RuleEvaluation {
  ruleId: string;
  targetEntity: string;
  fieldPath: string;
  conditionType: string;
  expectedValue?: any;
  /** The value read (scalar targets), or what the aggregate produced (collection targets). */
  actualValue?: any;
  matched: boolean;
  error?: string;
}

/* ------------------------------------------------------------------ */
/* Ordering                                                             */
/* ------------------------------------------------------------------ */

/** Evaluation order: priority ascending, then name (the backend tie-break). */
export const byEvaluationOrder = <T extends Pick<DefinitionRecord, "priority" | "name">>(a: T, b: T) =>
  a.priority - b.priority || a.name.localeCompare(b.name);

export const isCollectionTarget = (
  vocabulary: Pick<RuleVocabulary, "collectionTargets"> | null | undefined,
  targetEntity?: string
) => !!targetEntity && (vocabulary?.collectionTargets ?? []).includes(targetEntity);

/* ------------------------------------------------------------------ */
/* Conditions                                                           */
/* ------------------------------------------------------------------ */

export const CONDITIONS_WITHOUT_VALUE = new Set<RuleCondition>(["EXISTS", "NOT_EXISTS"]);

export const ARRAY_CONDITIONS = new Set<RuleCondition>(["IN", "NOT_IN"]);

export const NUMERIC_CONDITIONS = new Set<RuleCondition>([
  "GREATER_THAN",
  "LESS_THAN",
  "GREATER_THAN_OR_EQUAL",
  "LESS_THAN_OR_EQUAL",
]);

export const STRING_ONLY_CONDITIONS = new Set<RuleCondition>(["CONTAINS", "NOT_CONTAINS", "REGEX"]);

/** Conditions where the user may choose how a scalar is typed. */
export const TYPED_SCALAR_CONDITIONS = new Set<RuleCondition>(["EQUALS", "NOT_EQUALS", "IN", "NOT_IN"]);

/** Aggregates whose condition compares a count, so the expected value is a number. */
export const COUNT_AGGREGATES = new Set<string>(["COUNT", "COUNT_DISTINCT"]);

export const CONDITION_HELP: Record<RuleCondition, string> = {
  EQUALS: 'Strict equality. 1 is not "1"; choose the value type accordingly.',
  NOT_EQUALS: "Strict inequality.",
  IN: "Matches when the value (or any element of an array value) is in the list.",
  NOT_IN: "Matches when the value is not in the list.",
  CONTAINS: "String contains the substring, or array contains the value. Case-sensitive.",
  NOT_CONTAINS: "String does not contain the substring, or array does not contain the value.",
  GREATER_THAN: "Numeric compare. Both sides must be numbers.",
  LESS_THAN: "Numeric compare. Both sides must be numbers.",
  GREATER_THAN_OR_EQUAL: "Numeric compare. Both sides must be numbers.",
  LESS_THAN_OR_EQUAL: "Numeric compare. Both sides must be numbers.",
  EXISTS: "Value is present and not null. No expected value.",
  NOT_EXISTS: "Value is absent or null. No expected value.",
  REGEX: "JavaScript regex tested against a string value. An invalid pattern never matches.",
};

export const AGGREGATE_HELP: Record<string, string> = {
  COUNT_DISTINCT: "The condition compares the number of distinct non-empty values.",
  COUNT: "The condition compares the number of members with a value.",
  ANY: "The condition is applied per member; at least one must match.",
  ALL: "The condition is applied per member; all must match, and there must be at least one.",
  NONE: "The condition is applied per member; none may match. Holds for an empty collection.",
};

export const VALUE_AGGREGATE_HELP: Record<string, string> = {
  DISTINCT:
    "The single distinct value across the collection. Yields nothing when there are several, so the definition does not win.",
  FIRST: "The first value found.",
};

/* ------------------------------------------------------------------ */
/* Expected value: form text <-> typed payload                          */
/* ------------------------------------------------------------------ */

export type ExpectedValueType = "string" | "number" | "boolean";

export const EXPECTED_VALUE_TYPE_OPTIONS: { value: ExpectedValueType; label: string }[] = [
  { value: "string", label: "Text" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean (true / false)" },
];

const coerceScalar = (raw: string, type: ExpectedValueType): string | number | boolean => {
  const value = raw.trim();
  if (type === "number") return Number(value);
  if (type === "boolean") return value.toLowerCase() === "true";
  return value;
};

/** The type the expected value is sent as, given the condition and what the user chose. */
export const effectiveValueType = (
  conditionType: RuleCondition,
  valueType: ExpectedValueType = "string",
  countCompare = false
): ExpectedValueType =>
  NUMERIC_CONDITIONS.has(conditionType) || countCompare
    ? "number"
    : TYPED_SCALAR_CONDITIONS.has(conditionType)
    ? valueType
    : "string";

/**
 * Build the `expectedValue` payload for a rule from the free-text form input.
 * Shape depends on the condition: array for IN/NOT_IN, number for numeric
 * comparisons (and count aggregates), nothing for EXISTS/NOT_EXISTS, string
 * otherwise. Booleans and numbers are sent typed (`true`, not `"true"`).
 */
export const buildExpectedValue = (
  conditionType: RuleCondition,
  rawValue: any,
  valueType: ExpectedValueType = "string",
  aggregate?: string
): any => {
  if (CONDITIONS_WITHOUT_VALUE.has(conditionType)) return undefined;
  if (rawValue === undefined || rawValue === null || rawValue === "") return undefined;

  const raw = String(rawValue);
  const countCompare = !!aggregate && COUNT_AGGREGATES.has(aggregate);
  const scalarType = effectiveValueType(conditionType, valueType, countCompare);

  if (ARRAY_CONDITIONS.has(conditionType)) {
    return raw
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v.length > 0)
      .map((v) => coerceScalar(v, scalarType));
  }

  if (scalarType === "number") return Number(raw.trim());
  if (STRING_ONLY_CONDITIONS.has(conditionType)) return raw;
  return coerceScalar(raw, scalarType);
};

export const inferValueType = (value: any): ExpectedValueType => {
  if (typeof value === "number") return "number";
  if (typeof value === "boolean") return "boolean";
  return "string";
};

/** Inverse of {@link buildExpectedValue}: prefill the form from a stored rule. */
export const expectedValueToForm = (
  rule: Pick<RuleFields, "conditionType" | "expectedValue">
): { expectedValue?: string; valueType: ExpectedValueType } => {
  const { expectedValue } = rule;
  if (expectedValue === undefined || expectedValue === null) {
    return { expectedValue: undefined, valueType: "string" };
  }
  if (Array.isArray(expectedValue)) {
    return {
      expectedValue: expectedValue.map((v) => String(v)).join(", "),
      valueType: inferValueType(expectedValue[0]),
    };
  }
  return { expectedValue: String(expectedValue), valueType: inferValueType(expectedValue) };
};

/** Display helper for tables and evaluation results. */
export const formatExpectedValue = (value: any): string => {
  if (value === undefined || value === null) return "-";
  if (Array.isArray(value)) return value.map((v) => JSON.stringify(v)).join(", ");
  if (typeof value === "string") return value;
  return JSON.stringify(value);
};

/** Display helper for the value a rule actually read. */
export const formatActualValue = (value: any): string => {
  if (value === undefined) return "(absent)";
  if (value === null) return "null";
  if (typeof value === "string") return value === "" ? '""' : value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

/**
 * Validate the expected value as typed, before it is built. Returns the error
 * message, or `null` when the value is acceptable for the condition.
 */
export const validateExpectedValue = (input: {
  conditionType?: RuleCondition;
  raw: any;
  valueType?: ExpectedValueType;
  /** The condition compares a count (collection target with COUNT / COUNT_DISTINCT). */
  countCompare?: boolean;
}): string | null => {
  const { conditionType, valueType = "string", countCompare = false } = input;
  if (!conditionType || CONDITIONS_WITHOUT_VALUE.has(conditionType)) return null;

  const raw = input.raw === undefined || input.raw === null ? "" : String(input.raw).trim();
  if (!raw) return "Expected Value is required for this condition";

  const parts = ARRAY_CONDITIONS.has(conditionType)
    ? raw.split(",").map((p) => p.trim()).filter(Boolean)
    : [raw];
  if (parts.length === 0) return "Enter at least one value";

  const type = effectiveValueType(conditionType, valueType, countCompare);
  if (type === "number") {
    const bad = parts.find((p) => Number.isNaN(Number(p)));
    if (bad !== undefined) return `"${bad}" is not a number`;
  }
  if (type === "boolean") {
    const bad = parts.find((p) => !["true", "false"].includes(p.toLowerCase()));
    if (bad !== undefined) return `"${bad}" must be true or false`;
  }
  if (conditionType === "REGEX") {
    try {
      // eslint-disable-next-line no-new
      new RegExp(raw);
    } catch {
      return "Invalid regex pattern. An invalid pattern never matches.";
    }
  }
  return null;
};

/** What the expected-value input should say for a condition. */
export const expectedValueHint = (input: {
  conditionType: RuleCondition;
  valueType?: ExpectedValueType;
  countCompare?: boolean;
}): { placeholder: string; extra?: string } => {
  const { conditionType, valueType = "string", countCompare = false } = input;
  if (countCompare) {
    return { placeholder: "Enter a count", extra: "The condition compares a count, so the value is a number." };
  }
  if (ARRAY_CONDITIONS.has(conditionType)) {
    return { placeholder: "Comma-separated values (e.g. A, B, C)", extra: "Each value is sent as a list element." };
  }
  if (conditionType === "REGEX") return { placeholder: "Enter a JavaScript regex pattern (without slashes)" };
  if (NUMERIC_CONDITIONS.has(conditionType)) return { placeholder: "Enter a number" };
  if (STRING_ONLY_CONDITIONS.has(conditionType)) return { placeholder: "Enter text (case-sensitive)" };
  if (valueType === "boolean") return { placeholder: "true or false" };
  return { placeholder: "Enter expected value" };
};

/* ------------------------------------------------------------------ */
/* Definitions: the checks the list pages warn about                    */
/* ------------------------------------------------------------------ */

/** Priorities shared by more than one live definition (evaluation order between them falls back to name). */
export const sharedPriorities = <T extends Pick<DefinitionRecord, "priority" | "deleted">>(definitions: T[]) => {
  const counts = new Map<number, number>();
  definitions.filter((d) => !d.deleted).forEach((d) => counts.set(d.priority, (counts.get(d.priority) ?? 0) + 1));
  return Array.from(counts.entries())
    .filter(([, count]) => count > 1)
    .map(([priority]) => priority)
    .sort((a, b) => a - b);
};

/** The rules that count: active and not deleted. */
export const liveRules = <T extends Pick<RuleRecord, "active" | "deleted">>(rules: T[]) =>
  rules.filter((r) => r.active && !r.deleted);
